import React, { useState, useRef, useContext, useEffect, useMemo, memo, useCallback } from 'react';
import Stepper, { StepperHandles } from '../../../../../CustomComponent/StepperComponent/Stepper.Component';
import ProductTableComponent from '../ProductsdetailsTable/ProductTable.component';
import PackagingComponent from '../Packaging/Packaging.component';
import classes from './MultiStepper.module.css'
import { authContext } from '../../../../../context/AuthProvider.component';
import Transport from '../Transport/Transport.component';
import Disposal from '../Disposal/Disposal.component';
import LcaReport from '../LCAReport/LcaReport.component';
import useFetch from '../../../../../Customhooks/useFetch';
import { baseUrl } from '../../../../../Constants/constant';
import { useDisclosure } from '@mantine/hooks';
import DrawerComponent from '../../../DrawerComponent/Drawer.component';
import Utility from '../AddUtility/Utility.component';

// Memoized step components to prevent unnecessary re-renders
const MemoizedProductTable = memo(ProductTableComponent);
const MemoizedPackaging = memo(PackagingComponent);
const MemoizedTransport = memo(Transport);
const MemoizedDisposal = memo(Disposal);
const MemoizedUtility = memo(Utility);
const MemoizedLcaReport = memo(LcaReport);
const MemoizedDrawer = memo(DrawerComponent);

const MultiStepper: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [initialCompletedSteps, setInitialCompletedSteps] = useState<number[]>([]);
  const [drawerOpened, { open: opendrawer, close: closedrawer }] = useDisclosure(false);
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [isStepReady, setIsStepReady] = useState(false); // New state to control step rendering
  const isNavigatingRef = useRef(false);

  const stepperRef = useRef<StepperHandles>(null);
  const { computeId, draftLca } = useContext<any>(authContext);

  // Memoize token to prevent unnecessary re-renders
  const token = useMemo(() => localStorage.getItem("token"), []);

  // Memoize fetch configuration
  const fetchConfig = useMemo(() => ({
    method: "GET" as const,
    headers: {
      "Content-Type": "application/json",
      token: token,
    },
  }), [token]);

  const { data: getDraftLcaData, error: getDraftLcaError, fetchData: fetchDraftLca } = useFetch(fetchConfig);

  // Memoize steps array
  const steps = useMemo(() => [
    { label: 'Components', value: 0 },
    { label: 'Packaging', value: 1 },
    { label: 'Transport', value: 2 },
    { label: 'Disposal', value: 3 },
    { label: 'Comments', value: 4 },
    { label: 'Report', value: 5 }
  ], []);

  // Memoize step lookup for better performance
  const stepLookup = useMemo(() => 
    steps.reduce((acc, step) => {
      acc[step.label] = step.value;
      return acc;
    }, {} as Record<string, number>)
  , [steps]);

  // Optimized fetch effect with dependency array
  useEffect(() => {
    if (computeId && draftLca != null) {
      setIsStepReady(false); // Reset step ready state when fetching
      fetchDraftLca(`${baseUrl}compute/lca/${computeId}?is_draft=${draftLca}`);
    }
  }, [computeId, draftLca]);

  // Optimized draft data processing
  useEffect(() => {
    const draftStep = getDraftLcaData?.data?.rows?.[0]?.step;
    
    if (draftStep) {
      const stepValue = stepLookup[draftStep];
      
      if (stepValue !== undefined) {
        // Batch state updates to prevent multiple renders
        setActiveStep(prevStep => {
          if (stepValue !== prevStep) {
            // Calculate completed steps only when activeStep changes
            const completedSteps = Array.from({ length: stepValue }, (_, i) => i);
            
            setInitialCompletedSteps(prevCompleted => {
              // Only update if different
              if (prevCompleted.length !== completedSteps.length || 
                  !prevCompleted.every((step, index) => step === completedSteps[index])) {
                return completedSteps;
              }
              return prevCompleted;
            });
            
            return stepValue;
          }
          return prevStep;
        });
        
        // Set step ready after state updates
        setTimeout(() => setIsStepReady(true), 0);
      }
    } else if (getDraftLcaData && !draftStep) {
      // If no draft step, set to ready with default step
      setIsStepReady(true);
    }
  }, [getDraftLcaData, stepLookup]);

  // Reset state when computeId changes
  useEffect(() => {
    if (computeId === null) {
      setActiveStep(0);
      setInitialCompletedSteps([]);
      setIsStepReady(true); // Set ready for new computation
    }
  }, [computeId]);

  // Memoized callback functions
  const handleStepComplete = useCallback((step: number) => {
    const nextStep = Math.min(step + 1, steps.length - 1);
    setActiveStep(nextStep);
  }, [steps.length, activeStep, steps]);

  const handleStepBack = useCallback((step: number) => {
    const prevStep = Math.max(step - 1, 0);
    setActiveStep(prevStep);
  }, []);

  const handleStepChange = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    // Prevent multiple rapid calls
    if (isNavigatingRef.current) {
      return;
    }
    
    isNavigatingRef.current = true;
    stepperRef.current?.handleNextStep();
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 100);
  }, []);

  const goToPreviousStep = useCallback(() => {
    stepperRef.current?.handlePrevStep();
  }, []);

  const handleDrawerStateChange = useCallback((state: boolean) => {
    setDrawerIsOpen(state);
  }, []);

  // Memoize step rendering to prevent unnecessary re-renders
  const currentStepComponent = useMemo(() => {
    if (!computeId || !isStepReady) return null; // Added isStepReady condition

    switch (activeStep) {
      case 0:
        return <MemoizedProductTable onComplete={goToNextStep} />;
      case 1:
        return (
          <MemoizedPackaging
            onComplete={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 2:
        return (
          <MemoizedTransport
            onComplete={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 3:
        return (
          <MemoizedDisposal
            onComplete={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 4:
        return (
          <MemoizedUtility
            setDrawerState={handleDrawerStateChange}
            opendrawer={opendrawer}
            onBack={goToPreviousStep}
          />
        );
      case 5:
        return <MemoizedLcaReport />;
      default:
        return null;
    }
  }, [activeStep, computeId, isStepReady, goToNextStep, goToPreviousStep, handleDrawerStateChange, opendrawer]);

  // Memoize drawer component
  const drawerComponent = useMemo(() => {
    if (!drawerIsOpen) return null;
    
    return (
      <MemoizedDrawer
        onComplete={goToNextStep}
        isOpen={drawerOpened}
        isClose={closedrawer}
      />
    );
  }, [drawerIsOpen, drawerOpened, closedrawer, goToNextStep]);

  return (
    <div className={classes.SubCategorySection}>
      <Stepper
        ref={stepperRef}
        steps={steps}
        activeStep={activeStep}
        initialCompletedSteps={initialCompletedSteps}
        onStepComplete={handleStepComplete}
        onStepBack={handleStepBack}
        onStepChange={handleStepChange}
      />

      <div className={classes.componentsSection}>
        {currentStepComponent}
        {drawerComponent}
      </div>
    </div>
  );
};

export default memo(MultiStepper);